/**
 * ShapeRenderer
 *
 * Renders a LineShape as a 3D polygon (filled mesh + outline) in a THREE.Scene.
 * Uses local coordinate system to avoid floating point precision issues.
 * Handles geometry/material creation and disposal.
 */

import * as THREE from 'three';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LineShape } from './types';
import { Viewer } from '../../../../viewer/viewer';

/** Brand purple (French Violet) - used for the shape outline and selection highlight */
const LINE_COLOR = 0x854dd6;

/** Brand green (Neon Green) - used for the shape fill */
const FILL_COLOR = 0x7edd64;

export class ShapeRenderer {
  private shapeGroup: THREE.Group | null = null;
  private _currentShape: LineShape | null = null;

  constructor(
    private viewer: Viewer,
    private scene: THREE.Scene,
  ) {}

  /** The currently rendered shape, or null if none */
  get currentShape(): LineShape | null {
    return this._currentShape;
  }

  /**
   * Render a shape as a 3D polygon with fill + outline.
   * Uses the same visual style as rooms (green color scheme).
   */
  render(shape: LineShape): void {
    this.clear();

    if (shape.vertices.length < 3) return;

    this._currentShape = shape;

    // Use first vertex as local origin to avoid precision issues
    const localOrigin = shape.vertices[0].clone();

    // Convert vertices to local coordinates
    const localVertices = shape.vertices.map((v) => v.clone().sub(localOrigin));

    // Create shape geometry in local coordinates
    const vertices2D = localVertices.map((v) => new THREE.Vector2(v.x, v.y));
    const shape2D = new THREE.Shape(vertices2D);

    // Create group positioned at the local origin
    this.shapeGroup = new THREE.Group();
    this.shapeGroup.position.copy(localOrigin);

    // Average Z height for positioning
    const avgZ = localVertices.reduce((sum, v) => sum + v.z, 0) / localVertices.length;

    // Create filled mesh with highlight color
    const fillGeometry = new THREE.ShapeGeometry(shape2D);
    const fillMaterial = new THREE.MeshBasicMaterial({
      color: FILL_COLOR,
      transparent: true,
      opacity: 0.25,
      side: THREE.DoubleSide,
      depthTest: true,
      depthWrite: false,
    });

    const fillMesh = new THREE.Mesh(fillGeometry, fillMaterial);
    fillMesh.position.z = avgZ;
    fillMesh.renderOrder = 997;
    this.shapeGroup.add(fillMesh);

    // Create outline using Line2 in local coordinates
    const positions: number[] = [];
    for (const vertex of localVertices) {
      positions.push(vertex.x, vertex.y, vertex.z);
    }
    // Close the loop
    positions.push(localVertices[0].x, localVertices[0].y, localVertices[0].z);

    const lineGeometry = new LineGeometry();
    lineGeometry.setPositions(positions);

    const lineMaterial = new LineMaterial({
      color: LINE_COLOR,
      linewidth: 1,
      resolution: new THREE.Vector2(this.viewer.renderArea.clientWidth, this.viewer.renderArea.clientHeight),
      depthTest: true,
      transparent: true,
      opacity: 1.0,
    });

    const line = new Line2(lineGeometry, lineMaterial);
    line.renderOrder = 999;
    this.shapeGroup.add(line);

    // Add background line for selection feedback
    const backgroundLineMaterial = new LineMaterial({
      color: LINE_COLOR,
      linewidth: 15,
      resolution: new THREE.Vector2(this.viewer.renderArea.clientWidth, this.viewer.renderArea.clientHeight),
      transparent: true,
      depthTest: true,
      opacity: 0.25,
    });

    const backgroundLine = new Line2(lineGeometry.clone(), backgroundLineMaterial);
    backgroundLine.renderOrder = 998;
    this.shapeGroup.add(backgroundLine);

    // Add to scene
    this.scene.add(this.shapeGroup);
  }

  /**
   * Clear shape rendering and dispose all resources.
   */
  clear(): void {
    if (this.shapeGroup) {
      this.scene.remove(this.shapeGroup);
      this.shapeGroup.traverse((obj) => {
        if (obj instanceof THREE.Mesh || obj instanceof THREE.Line) {
          obj.geometry.dispose();
          if (Array.isArray(obj.material)) {
            obj.material.forEach((mat) => mat.dispose());
          } else {
            obj.material.dispose();
          }
        }
      });
      this.shapeGroup = null;
    }
    this._currentShape = null;
  }
}
